import { CircularProgress } from "@mui/material";
import Button, { type ButtonProps } from "@mui/material/Button";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import { isNoU } from "Utils/ObjectUtils";
import styled from "styled-components";

export interface PrimaryButtonProps extends ButtonProps {
  paddingLeftRight?: number;
  isLoading?: boolean;
  fetchProgress?: number | null;
}

const StyledButton = styled(
  ({ paddingLeftRight, fetchProgress, ...rest }: PrimaryButtonProps) => (
    <Button {...rest} />
  ),
)`
  padding: 15px ${(props) => props.theme.spacing(props.paddingLeftRight ?? 2)};
  min-width: 130px;

  /* Without this, size="small" shrinks only the label: the padding and min-width above
     are more specific than MUI's own size classes, so the button stays full height. */
  ${(props) =>
    props.size === "small" &&
    `
      padding: ${props.theme.spacing(2, props.paddingLeftRight ?? 5)};
      min-width: auto;
    `}
  border: ${(props) =>
    props.variant === "contained" ? "1px solid transparent" : ""};
  box-shadow: ${(props) =>
    props.variant !== "text" && props.theme.shadow.primary};
  font-weight: ${(props) => props.variant === "text" && 600};

  ${(props) =>
    props.color === "primary" &&
    `
      color: ${props.theme.palette.common.white};
    `}

  ${(props) =>
    props.color === "secondary" &&
    `
      background-color: ${props.theme.palette.common.white};
      border: 1px solid ${props.theme.palette.primary.main};
      color: ${props.theme.palette.text.primary};
      box-shadow: none;

      &:hover {
        background-color: ${props.theme.palette.primary.main};
        color: ${props.theme.palette.primary.contrastText};
      }
    `}

    ${(props) =>
      props.color === "error" &&
      ` 
        border: 1px solid ${props.theme.palette.error.main} !important;
      `}

  &.Mui-disabled {
    color:  ${(props) => (props.color === "error" ? props.theme.palette.error.main : props.theme.palette.common.white)};
    background: ${(props) =>
      props.color === "info" || props.color === "error"
        ? "none;"
        : props.theme.palette.action.disabledBackground};
  }

  .MuiButton-label {
    z-index: 1;
  }
`;

const StyledLoadingIconWrapper = styled.div`
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  background-color: ${({ theme: { palette }, color }) =>
    color === "error"
      ? palette.error.contrastText
      : palette.action.disabledBackground};
  border-radius: ${({ theme }) => theme.borderRadius};
`;

const StyledProgress = styled(PrimarySkeleton)`
  &.MuiSkeleton-root {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    background-color: ${(props) => props.theme.palette.primary.main};
    z-index: -1;
  }
`;

export const PrimaryButton = (props: PrimaryButtonProps) => {
  const { isLoading, fetchProgress, variant, disabled, ...rest } = props;

  return (
    <StyledButton
      paddingLeftRight={props.paddingLeftRight}
      fetchProgress={fetchProgress}
      {...rest}
      color={props.color ?? "secondary"}
      variant={variant ?? "contained"}
      disabled={disabled || isLoading}
    >
      {isLoading && (
        <StyledLoadingIconWrapper
          color={props.color === "primary" ? "inherit" : props.color}
        >
          <CircularProgress
            size={18}
            color={props.color === "primary" ? "inherit" : props.color}
          />
        </StyledLoadingIconWrapper>
      )}
      {props.children}
      {!isNoU(fetchProgress) && <StyledProgress width={`${fetchProgress}%`} />}
    </StyledButton>
  );
};
